// Visitor country detection for preselecting the phone dial code.
// Priority: device timezone -> browser locale -> backend /api/geo (IP) -> US.
// Timezone is the most reliable signal (a phone in NL says Europe/Amsterdam
// even behind a VPN), so the server's IP answer is only a fallback.
import { COUNTRY_CODES } from "./mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISO_SET = COUNTRY_CODES.reduce((acc, c) => {
  acc[c.code] = 1;
  return acc;
}, {});

// Timezone -> ISO2 (broad coverage of common IANA zones)
const TZ_ISO = {
  "America/New_York": "US", "America/Detroit": "US", "America/Chicago": "US",
  "America/Denver": "US", "America/Phoenix": "US", "America/Los_Angeles": "US",
  "America/Anchorage": "US", "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA",
  "America/Mexico_City": "MX", "America/Monterrey": "MX", "America/Tijuana": "MX",
  "America/Sao_Paulo": "BR", "America/Bahia": "BR", "America/Fortaleza": "BR",
  "America/Argentina/Buenos_Aires": "AR", "America/Santiago": "CL",
  "America/Bogota": "CO", "America/Lima": "PE", "America/Caracas": "VE",
  "America/Guayaquil": "EC", "America/Montevideo": "UY", "America/Asuncion": "PY",
  "America/La_Paz": "BO", "America/Costa_Rica": "CR", "America/Panama": "PA",
  "America/Santo_Domingo": "DO", "America/Guatemala": "GT", "America/Jamaica": "JM",
  "Europe/London": "GB", "Europe/Dublin": "IE", "Europe/Paris": "FR",
  "Europe/Berlin": "DE", "Europe/Amsterdam": "NL", "Europe/Brussels": "BE",
  "Europe/Luxembourg": "LU", "Europe/Zurich": "CH", "Europe/Vienna": "AT",
  "Europe/Madrid": "ES", "Europe/Lisbon": "PT", "Europe/Rome": "IT",
  "Europe/Athens": "GR", "Europe/Stockholm": "SE", "Europe/Oslo": "NO",
  "Europe/Copenhagen": "DK", "Europe/Helsinki": "FI", "Atlantic/Reykjavik": "IS",
  "Europe/Warsaw": "PL", "Europe/Prague": "CZ", "Europe/Bratislava": "SK",
  "Europe/Budapest": "HU", "Europe/Bucharest": "RO", "Europe/Sofia": "BG",
  "Europe/Zagreb": "HR", "Europe/Ljubljana": "SI", "Europe/Belgrade": "RS",
  "Europe/Kiev": "UA", "Europe/Kyiv": "UA", "Europe/Moscow": "RU",
  "Europe/Tallinn": "EE", "Europe/Riga": "LV", "Europe/Vilnius": "LT",
  "Asia/Nicosia": "CY", "Europe/Malta": "MT", "Europe/Istanbul": "TR",
  "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Qatar": "QA",
  "Asia/Kuwait": "KW", "Asia/Bahrain": "BH", "Asia/Muscat": "OM",
  "Asia/Jerusalem": "IL", "Africa/Cairo": "EG", "Africa/Casablanca": "MA",
  "Africa/Algiers": "DZ", "Africa/Tunis": "TN", "Africa/Lagos": "NG",
  "Africa/Nairobi": "KE", "Africa/Accra": "GH", "Africa/Johannesburg": "ZA",
  "Africa/Addis_Ababa": "ET", "Africa/Kampala": "UG", "Africa/Dar_es_Salaam": "TZ",
  "Africa/Lusaka": "ZM", "Africa/Harare": "ZW", "Africa/Douala": "CM",
  "Africa/Abidjan": "CI", "Africa/Dakar": "SN", "Africa/Luanda": "AO",
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Karachi": "PK",
  "Asia/Dhaka": "BD", "Asia/Colombo": "LK", "Asia/Kathmandu": "NP",
  "Asia/Singapore": "SG", "Asia/Kuala_Lumpur": "MY", "Asia/Jakarta": "ID",
  "Asia/Bangkok": "TH", "Asia/Ho_Chi_Minh": "VN", "Asia/Manila": "PH",
  "Asia/Phnom_Penh": "KH", "Asia/Yangon": "MM", "Asia/Almaty": "KZ",
  "Asia/Shanghai": "CN", "Asia/Hong_Kong": "HK", "Asia/Taipei": "TW",
  "Asia/Tokyo": "JP", "Asia/Seoul": "KR",
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Brisbane": "AU",
  "Australia/Perth": "AU", "Australia/Adelaide": "AU", "Pacific/Auckland": "NZ",
};

// device-side country guess: timezone first, locale second
export function guessDeviceIso() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (TZ_ISO[tz] && ISO_SET[TZ_ISO[tz]]) return TZ_ISO[tz];
  } catch (e) {}
  try {
    const langs = navigator.languages || [navigator.language || ""];
    for (let i = 0; i < langs.length; i++) {
      const m = /[-_]([a-z]{2})$/i.exec(langs[i] || "");
      const iso = m ? m[1].toUpperCase() : "";
      if (iso && ISO_SET[iso]) return iso;
    }
  } catch (e) {}
  return "";
}

// Resolve the visitor's ISO2: device guess instantly, else backend IP lookup.
// Cached in sessionStorage. Returns a Promise<string> (ISO2, defaults to "US").
export async function detectCountryIso() {
  const guess = guessDeviceIso();
  if (guess) return guess;
  try {
    const cached = sessionStorage.getItem("tmu_geo_iso");
    if (cached && ISO_SET[cached]) return cached;
    const res = await fetch(`${API}/geo`);
    const g = await res.json();
    const iso = (g && g.country_code ? g.country_code : "").toUpperCase();
    if (iso && ISO_SET[iso]) {
      sessionStorage.setItem("tmu_geo_iso", iso);
      return iso;
    }
  } catch (e) {}
  return "US";
}
